import { useState, useEffect } from 'react';
import { Edit, SimpleForm, TextField, NumberField, FunctionField, usePermissions, useDataProvider, TopToolbar, useTranslate } from 'react-admin';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import { useTheme, useMediaQuery, Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { buildListFilterUrl } from '@/helpers/filters/buildListFilterUrl.js';
import { setPendingFilterReferenceRecord } from '@/helpers/filters/filterReferenceSeed.js';
import { OnlyBulkDeleteActionButton } from '@/components/global/buttons/bulkButtons';
import CustomList from '@/components/global/list/CustomList.jsx';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import { ContractorMoreActions } from '@/components/contractors/ContractorShow.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import CustomExportButton from '@/components/global/buttons/CustomExportButton.jsx';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import getLoadingState from '@/helpers/ui/getLoadingState.jsx';
import { useDrawer } from '@/composables/useDrawer';
import { RightDrawer } from '@/components/global/drawers/RightDrawer';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput';
import { ContractorLogoField } from '@/components/contractors/ContractorLogoField.jsx';
import ContractorAvgTransactionsField from '@/components/contractors/ContractorAvgTransactionsField.jsx';
import { CachedContractorTopTagsField } from '@/components/contractors/ContractorTopTagsField.jsx';
import ContractorAvgAppRateField from '@/components/contractors/ContractorAvgAppRateField.jsx';
import ContractorOperatorsField from '@/components/contractors/ContractorOperatorsField.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import TableHeaderCellWithSubtitle from '@/components/global/fields/TableHeaderCellWithSubtitle.jsx';
import CustomDatagridHeader from '@/components/global/list/CustomDatagridHeader.jsx';
import { ContractorUserMasterField } from '@/components/contractors/ContractorUserMasterField.jsx';
import { ContractorEditBasicInfo } from '@/components/contractors/ContractorEditBasicInfo.jsx';
import { ContractorEditSettings } from '@/components/contractors/ContractorEditSettings.jsx';
import TagCategoryToolbar from '@/components/global/tags/TagCategoryToolbar';
import CachedDataActionButton from '@/components/global/buttons/CachedDataActionButton.jsx';

const ListActions = () => {
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();
    if (isLoading) return null;

    return (
        <>
            {!isMobile && (
                <TopToolbar>
                    <SearchFilterInput />
                    <CustomSelectColumnsButton startIcon={<ColumnsIcon />} style={{ margin: '0px -4px' }} />
                    {(permissions.isAdmin || permissions.isServiceMaster) && (
                        <CustomToolbarActionsPopover>
                            <CustomCreateButton label={translate('resources.contractor.labels.createContractor')} />
                            <CustomExportButton />
                            {permissions.isAdmin && <CachedDataActionButton resource='contractor' type={null} />}
                        </CustomToolbarActionsPopover>
                    )}
                </TopToolbar>
            )}
        </>
    );
};

const Form = ({ action = 'create', handleClose }) => {
    const translate = useTranslate();

    return (
        <SimpleForm
            toolbar={<TagCategoryToolbar onCancel={handleClose} cancelLabel={translate('common.cancel')} saveLabel={translate('common.save')} />}
            sx={{ marginBottom: '34px!important', '& .RaAutocompleteInput-textField': { minWidth: '200px' } }}
        >
            <ContractorEditBasicInfo action={action} />
            <ContractorEditSettings action={action} />
        </SimpleForm>
    );
};

Form.propTypes = {
    action: PropTypes.oneOf(['create', 'edit']),
    handleClose: PropTypes.func,
};

const ClickableField = ({ children, navigateTo, onClick, filterRecord, filterSource }) => {
    const navigate = useNavigate();

    const handleClick = e => {
        e.stopPropagation();
        if (onClick) {
            onClick(e);
        } else if (navigateTo) {
            if (filterRecord && filterSource) {
                setPendingFilterReferenceRecord(filterSource, filterRecord);
            }
            navigate(navigateTo);
        }
    };

    return (
        <Box
            sx={{
                cursor: 'pointer',
                width: 'fit-content',
                '&:hover': {
                    textDecoration: 'underline',
                },
            }}
            onClick={handleClick}
        >
            {children}
        </Box>
    );
};

ClickableField.propTypes = {
    children: PropTypes.node,
    navigateTo: PropTypes.string,
    onClick: PropTypes.func,
    filterRecord: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    filterSource: PropTypes.string,
};

const createContractorFilterUrl = (path, record, filterPath = 'contractorId_in') => buildListFilterUrl(path, { [filterPath]: [record.id] }, [filterPath]);

export const ContractorList = () => {
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const translate = useTranslate();

    if (isLoading) return null;

    return (
        <CustomList filters={[]} actions={<ListActions />} sort={{ field: 'updatedAt', order: 'DESC' }} addLastColumnShadow={true}>
            <CustomDatagridConfigurable
                bulkActionButtons={permissions.isAdmin ? <OnlyBulkDeleteActionButton /> : false}
                header={<CustomDatagridHeader />}
                rowClick={id => {
                    openDrawer(id, 'contractor-show');
                    return false;
                }}
            >
                <FunctionField
                    className='contractor-logo-field'
                    sortable={false}
                    columnLabel={translate('resources.contractor.fields.currentLogo')}
                    render={record => <ContractorLogoField record={record} />}
                />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('resources.contractor.labels.contractorName')} />}
                    sortBy='name'
                    render={() => <TextField source='name' />}
                />
                <NumberField
                    source='devicesCount'
                    label={translate('resources.deviceGroup.filters.deviceCount')}
                    render={record => (
                        <ClickableField filterRecord={record} filterSource='contractorId_in' navigateTo={createContractorFilterUrl('/device', record)}>
                            <NumberField source='devicesCount' record={record} />
                        </ClickableField>
                    )}
                    marginLeft='auto'
                />
                <FunctionField
                    label={
                        <TableHeaderCellWithSubtitle
                            title={translate('resources.contractor.labels.avgTransactionsTitle')}
                            subTitle={translate('resources.contractor.labels.avgTransactionsSubtitle')}
                        />
                    }
                    sortBy='dailyTransactionsCount'
                    render={record => (
                        <ClickableField
                            filterRecord={record}
                            filterSource='collectionPoint->contractor->id_in'
                            navigateTo={createContractorFilterUrl('/transaction', record, 'collectionPoint->contractor->id_in')}
                        >
                            <ContractorAvgTransactionsField record={record} />
                        </ClickableField>
                    )}
                />
                <FunctionField
                    label={
                        <TableHeaderCellWithSubtitle
                            title={translate('common.transactionsCount')}
                            subTitle={translate('resources.contractor.labels.transactionsSubtitle')}
                        />
                    }
                    sortBy='transactionsCount'
                    render={record => (
                        <ClickableField
                            filterRecord={record}
                            filterSource='collectionPoint->contractor->id_in'
                            navigateTo={createContractorFilterUrl('/transaction', record, 'collectionPoint->contractor->id_in')}
                        >
                            <NumberField source='transactionsCount' record={record} />
                        </ClickableField>
                    )}
                    marginLeft='auto'
                />
                <FunctionField
                    label={
                        <TableHeaderCellWithSubtitle
                            title={translate('resources.contractor.labels.appScanTitle')}
                            subTitle={translate('resources.contractor.labels.appScanSubtitle')}
                        />
                    }
                    sortBy='transactionsAppRate'
                    render={record => <ContractorAvgAppRateField record={record} />}
                />
                <FunctionField
                    label={
                        <TableHeaderCellWithSubtitle
                            title={translate('common.bagsCount')}
                            subTitle={translate('resources.contractor.labels.transactionsSubtitle')}
                        />
                    }
                    sortBy='bagsCount'
                    render={record => (
                        <ClickableField
                            filterRecord={record}
                            filterSource='collectionPoint->contractor->id_in'
                            navigateTo={createContractorFilterUrl('/bag', record, 'collectionPoint->contractor->id_in')}
                        >
                            <NumberField source='bagsCount' record={record} />
                        </ClickableField>
                    )}
                    marginLeft='auto'
                />
                <FunctionField
                    label={
                        <TableHeaderCellWithSubtitle
                            title={translate('resources.contractor.labels.payoutSumTitle')}
                            subTitle={translate('resources.contractor.labels.payoutSumSubtitle')}
                        />
                    }
                    sortBy='transactionsPayoutDetailsCash'
                    render={() => <NumberField source='transactionsPayoutDetailsCash' />}
                    align='right'
                />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('resources.contractor.labels.topTagsTitle')} />}
                    render={record => <CachedContractorTopTagsField record={record} />}
                    sortable={false}
                />
                <FunctionField
                    label={
                        <TableHeaderCellWithSubtitle
                            title={translate('resources.contractor.labels.serviceRequestsTitle')}
                            subTitle={translate('resources.contractor.labels.transactionsSubtitle')}
                        />
                    }
                    sortBy='serviceRequestsCount'
                    render={record => (
                        <ClickableField
                            filterRecord={record}
                            filterSource='device->collectionPoint->contractor->id_in'
                            navigateTo={createContractorFilterUrl('/service-request', record, 'device->collectionPoint->contractor->id_in')}
                        >
                            <NumberField source='serviceRequestsCount' record={record} />
                        </ClickableField>
                    )}
                    marginLeft='auto'
                />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('resources.contractor.labels.userMasterTitle')} />}
                    render={record => <ContractorUserMasterField record={record} />}
                    sortBy='userMaster.lastName'
                />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('resources.contractor.fields.operators')} />}
                    render={record => <ContractorOperatorsField record={record} />}
                    sortBy='operatorsCount'
                />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('common.options')} />}
                    render={() => (
                        <Box onClick={e => e.stopPropagation()}>
                            <ContractorMoreActions />
                        </Box>
                    )}
                />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};

export const ContractorCreate = ({ handleClose = () => {} }) => {
    const { isLoading } = usePermissions();
    const translate = useTranslate();
    if (isLoading) return null;

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.contractor.labels.createContractor')}>
            <CustomCreate handleClose={handleClose}>
                <Form handleClose={handleClose} />
            </CustomCreate>
        </RightDrawer>
    );
};

export const ContractorEdit = ({ handleClose, elementId }) => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const dataProvider = useDataProvider();
    const [contractor, setContractor] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const loadingState = getLoadingState({ isLoading, error, data: contractor, translate });

    useEffect(() => {
        if (elementId) {
            setIsLoading(true);
            dataProvider
                .getOne('contractor', { id: elementId })
                .then(({ data }) => {
                    setContractor(data);
                    setError(null);
                })
                .catch(err => {
                    console.error('Error fetching contractor:', err);
                    setError(err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [dataProvider, elementId]);

    if (loadingState) return loadingState;

    if (isLoading || !permissions) return null;
    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.contractor.labels.editContractor')}>
            <Edit
                id={elementId}
                resource='contractor'
                mutationMode='pessimistic'
                actions={false}
                toolbar={false}
                transform={data => {
                    if (typeof data.logo === 'string') {
                        data.logo = null;
                    }
                    return data;
                }}
                redirect={false}
            >
                <Form action='edit' handleClose={handleClose} />
            </Edit>
        </RightDrawer>
    );
};
